import { cva, type VariantProps } from "class-variance-authority"
import type * as React from "react"
import { cn } from "@/lib/utils"

const statusBadgeVariants = cva(
  "inline-flex h-5 w-fit shrink-0 items-center gap-1.5 rounded-md border px-1.5 text-xs font-medium whitespace-nowrap [&>svg]:size-3",
  {
    variants: {
      tone: {
        neutral: "bg-muted/60 text-muted-foreground border-border",
        // foreground text keeps it apart from `danger` when the accent is a red (brand raspberry)
        accent: "bg-primary/[0.07] text-foreground border-primary/25",
        success: "bg-success/10 text-success border-success/25",
        warning:
          "bg-warning/12 text-[color-mix(in_oklch,var(--warning),var(--foreground)_35%)] border-warning/30",
        danger: "bg-destructive/10 text-destructive border-destructive/25",
        info: "bg-info/10 text-info border-info/25",
      },
    },
    defaultVariants: { tone: "neutral" },
  },
)

const dotColor: Record<NonNullable<StatusTone>, string> = {
  neutral: "bg-muted-foreground/60",
  accent: "bg-primary",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-destructive",
  info: "bg-info",
}

export type StatusTone = VariantProps<typeof statusBadgeVariants>["tone"]

/** Compact semantic badge for states such as Active / Archived / Failed. */
export function StatusBadge({
  tone = "neutral",
  dot = false,
  pulse = false,
  className,
  children,
  ...props
}: React.ComponentProps<"span"> & { tone?: StatusTone; dot?: boolean; pulse?: boolean }) {
  return (
    <span data-slot="status-badge" className={cn(statusBadgeVariants({ tone }), className)} {...props}>
      {dot && (
        <span className="relative flex size-1.5">
          {pulse && (
            <span
              className={cn(
                "absolute inline-flex size-full animate-ping rounded-full opacity-60",
                dotColor[tone ?? "neutral"],
              )}
            />
          )}
          <span className={cn("relative inline-flex size-1.5 rounded-full", dotColor[tone ?? "neutral"])} />
        </span>
      )}
      {children}
    </span>
  )
}
